import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../firebase";
import PublicNavbar from "../../components/PublicNavbar";
import Footer from "../../components/Footer";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setCurrentUser);
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) {
      setUserRole(null);
      return;
    }
    const unsub = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
      const data = snap.exists() ? snap.data() : null;
      setUserRole(data?.shiftsRole || (data?.product === "buildmyshifts" ? data?.role : null) || null);
    });
    return () => unsub();
  }, [currentUser?.uid]);

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      setError('Type DELETE to confirm.');
      return;
    }

    setDeleting(true);
    setError("");

    try {
      const deleteOwnAccount = httpsCallable(functions, "shifts_deleteOwnAccount");
      await deleteOwnAccount();
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Delete BuildMyShifts account error:", err);
      const message = err?.message || "";
      if (message.includes("Only admins can delete BuildMyShifts accounts")) {
        setError("Only admins can delete BuildMyShifts accounts.");
      } else if (message.includes("only active admin")) {
        setError("You are the only active admin. Add another admin first, or delete the company account from Subscription Management.");
      } else {
        setError(message || "Failed to delete your BuildMyShifts account.");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-6">Delete your BuildMyShifts account</h1>

        {!currentUser ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <p className="text-gray-800">
              Sign in to delete your BuildMyShifts account directly from this page.
            </p>
            <p className="text-gray-700">
              If you are not signed in, you can still contact <a className="text-blue-600 hover:underline" href="mailto:support@buildmyshifts.com">support@buildmyshifts.com</a>.
            </p>
          </div>
        ) : userRole !== "admin" ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <p className="text-gray-800">
              Only BuildMyShifts admins can delete accounts.
            </p>
            <p className="text-gray-700">
              Contact your company admin if this account needs to be removed.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 space-y-3">
              <h2 className="text-xl font-semibold text-amber-900">What this deletes</h2>
              <p className="text-amber-900">
                This removes your BuildMyShifts account access and BuildMyShifts profile data.
              </p>
              <p className="text-amber-900">
                If you also use BuildMyChecks, that account is kept. Only your BuildMyShifts access is removed.
              </p>
              <p className="text-amber-900">
                If you are the only active admin for this company, you must add another admin first or delete the company account from Subscription Management.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <p className="text-gray-800">
                Signed in as <strong>{currentUser.email}</strong>
              </p>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Type <strong>DELETE</strong> to confirm
                </span>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900"
                  placeholder="DELETE"
                  disabled={deleting}
                />
              </label>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate(-1)}
                  disabled={deleting}
                  className="px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? "Deleting account..." : "Delete BuildMyShifts account"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
